/**
 * An export control that reports — RD-17. See `lib/download.ts` for the why.
 *
 * Four states, and each says something different: at rest it is the label,
 * while working it is a spinner, the word `Preparing…` **and** the bytes so far,
 * when it finishes it says so, and on a refusal it says why. The working state
 * matters more than it looks — it covers the gap between the click and the
 * server's first chunk, which on a large dataset is most of the wait and was
 * previously indistinguishable from nothing happening.
 *
 * ### Why `Preparing…` stays up while the bytes climb
 *
 * Nilay, 17 Sep: *"show both preparing as well as changing size for feedback."*
 * The first version swapped the word out for the size on the first chunk, which
 * read as two unrelated states — and the size alone answers *how much* without
 * answering *is this still going*. A count that is climbing is only legible as
 * progress next to something that says the work is not done.
 *
 * ### Why the button promises the save
 *
 * The download is a `fetch`, so the browser's own download UI shows nothing at
 * all until the last byte, when we hand it a blob. Anyone who has used a normal
 * download link expects the browser to react on click. The line beside the
 * button is that expectation, corrected: **it saves itself when it is ready**,
 * and then the `Downloaded` state confirms it actually happened. Without the
 * confirmation the label would simply snap back to `Export CSV`, which is what
 * a failed click also looks like.
 *
 * The counter is inside an `aria-live="polite"` button, or a screen reader is
 * told nothing at all and the control is a button that appears to do nothing
 * for eight seconds.
 */
import { useEffect, useRef, useState } from 'react'

import { downloadWithProgress, formatBytes } from '../lib/download'
import { Note } from './ui'

/** How long the `Downloaded` confirmation holds before the button resets. */
const DONE_MS = 6_000

type State =
  | { kind: 'rest' }
  | { kind: 'working'; bytes: number }
  | { kind: 'done'; bytes: number }
  | { kind: 'error'; message: string }

export function DownloadButton({
  url,
  filename,
  children,
  style,
  className,
}: {
  url: string
  /** Used only if the server sends no `Content-Disposition`. */
  filename: string
  children: React.ReactNode
  style?: React.CSSProperties
  className?: string
}) {
  const [state, setState] = useState<State>({ kind: 'rest' })
  // Guards against a second click while the first is in flight: the request is
  // not idempotent in cost — it re-runs the query and re-streams the rows.
  const busy = useRef(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The confirmation is on a timer, so it has to be cancelled if the card
  // unmounts underneath it — switching source tab mid-export does exactly that.
  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  async function run() {
    if (busy.current) return
    busy.current = true
    if (resetTimer.current) clearTimeout(resetTimer.current)
    setState({ kind: 'working', bytes: 0 })
    try {
      const { bytes } = await downloadWithProgress(url, filename, (received) =>
        setState({ kind: 'working', bytes: received }),
      )
      setState({ kind: 'done', bytes })
      resetTimer.current = setTimeout(() => setState({ kind: 'rest' }), DONE_MS)
    } catch (error) {
      setState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Export failed',
      })
    } finally {
      busy.current = false
    }
  }

  // The status line sits **beside** the button, not under it. Under it, the
  // action row grows by a line the moment anyone clicks, and every card below
  // jumps down — an export that shoves the page around while you read it is
  // worse feedback than none. Sideways it only consumes slack the row has.
  const aside =
    state.kind === 'working'
      ? 'Saves automatically when ready.'
      : state.kind === 'done'
        ? 'Saved to your downloads.'
        : null

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center gap-2">
        {aside && (
          <span className="text-xs" style={{ color: 'var(--text-label)', whiteSpace: 'nowrap' }}>
            {aside}
          </span>
        )}
        <button
          type="button"
          onClick={run}
          disabled={state.kind === 'working'}
          className={className}
          style={{
            cursor: state.kind === 'working' ? 'progress' : 'pointer',
            whiteSpace: 'nowrap',
            ...style,
          }}
          // The button's own text changes, so it is the live region. Polite, not
          // assertive: a byte counter interrupting a screen reader every chunk
          // would be unusable.
          aria-live="polite"
        >
          {state.kind === 'working' ? (
            <span className="inline-flex items-center gap-1.5">
              <Spinner />
              <span>Preparing…</span>
              {/* Zero bytes is not information — the count only appears once
                  there is something to count, so the first chunk arriving is
                  itself visible. Tabular figures, or the label jitters on every
                  chunk as the digits change width. */}
              {state.bytes > 0 && <span className="is-num">{formatBytes(state.bytes)}</span>}
            </span>
          ) : state.kind === 'done' ? (
            <span className="inline-flex items-center gap-1.5">
              <CheckMark />
              <span>Downloaded</span>
              <span className="is-num">{formatBytes(state.bytes)}</span>
            </span>
          ) : (
            children
          )}
        </button>
      </span>
      {state.kind === 'error' && (
        <span style={{ maxWidth: '22rem' }}>
          {/* RD-13: an export refuses **by name**. This used to be a 403 the
              browser wrote to disk as a file. */}
          <Note tone="error">{state.message}</Note>
        </span>
      )}
    </span>
  )
}

/**
 * `currentColor` throughout, because this button is rendered both filled
 * (brand ink on surface) and outlined, and a hardcoded stroke would be
 * invisible on one of them. Decorative — the button text carries the meaning.
 */
function Spinner() {
  return (
    <svg
      className="dl-spinner"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path
        d="M8 1.5A6.5 6.5 0 0 1 14.5 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function CheckMark() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 8.5 6.5 12 13 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
